import * as React from 'react';
import {
    RadioButtonWithLabelGroupWidget,
    TabStyles,
    DropdownButtonType,
    SingleSelectDropdown,
    ListItem,
    List,
    ListProps,
    TextFieldWidget,
    TextInputType
} from '@tableau/widgets';
import { cssClass } from '~src/style';
import { Messages } from '~src/codegen/Localize';
import { IOAuthCredential } from '~src/ts/ServerApi/types/IOAuthCredential';
import _ = require('underscore');

const option: string = cssClass('edit-oauth-connection', 'embedded-option', {
    marginBottom: TabStyles.Sizing.BaseUnit * 2,
});

const optionText: string = cssClass('edit-oauth-connection', 'option-text', {
    marginBottom: TabStyles.Sizing.BaseUnit,
});

const optionSubText: string = cssClass('edit-oauth-connection', 'option-subtext', {
    color: TabStyles.Typography.Color.Secondary
});

const rowClassname = cssClass('edit-connection', 'row', {
    width: '100%'
});

export type EditSalesforceConnectionDialogEmbeddedToggleProps = Readonly<{
    credentialNoValueOptionText: string;
    currentConnectionTypeDisplayName: string;
    addNewOAuthCredentialOptionValue: string;
    credentialId: string;
    credentials: IOAuthCredential[];
    credentialOption: string;
    username: string;
    password: string;
    handleUserNameChange: (username: string) => void;
    handlePasswordChange: (password: string) => void;
    handleToggleChange: (credentialOptionName: string) => void;
    handleCredentialIdChange: (newCredentialId: string) => void;
}>;

export function EditSalesforceConnectionDialogEmbeddedToggle(props: EditSalesforceConnectionDialogEmbeddedToggleProps) {

    const authenticationLabel: string = cssClass('edit-oauth-connection', 'authentication-label', {
        marginTop: TabStyles.Sizing.BaseUnit * 3,
        marginBottom: TabStyles.Sizing.BaseUnit * 2,
        fontWeight: 'bold'
    });

    const renderUsernamePassword = (): React.ReactNode => {
        return (
            < div className={option}>
                <div className={optionText}>{Messages.editDataConnections_salesforce_embedPassword()}</div>
                {renderEmbeddedPasswordOptionSelector()}
            </div>
        );
    };

    const renderOAuthCredentialOption = (): React.ReactNode => {
        return (
            < div className={option}>
                <div className={optionText}>{Messages.editDataConnections_salesforce_embedCredential()}</div>
                {renderOAuthCredentialOptionSelector()}
            </div>
        );
    };

    function getNameForIOAuthCredentialId(value: string, items: ListItem[]): React.ReactNode {
        if (!items.find(item => item.value === value)) {
            return props.credentialNoValueOptionText || Messages.selectOAuthCredential();
        } else {
            return items.find(item => item.value === value)?.content;
        }
    }

    function getIOAuthCredentialIdsList(ioauthCredentials: IOAuthCredential[]): ListItem[] {
        return (ioauthCredentials || []).map(ioac => ({
            value: ioac.id,
            content: ioac.role ? `${ioac.username} (${ioac.role})` : ioac.username
        }));
    }

    const renderEmbeddedPasswordOptionSelector = (): React.ReactNode => {
        if (props.credentialOption !== 'password') {
            return null;
        }

        /**
         * The style and the className end up going to different elements, which is why it was necessary to declare width: "100%" twice.
         */

        return (
            <>
                <div className={rowClassname} >
                    <div className={rowClassname}>
                        <TextFieldWidget
                            className={rowClassname}
                            style={{ width: '100%' }}
                            onChange={e => props.handleUserNameChange(e.target.value)}
                            testId={'username'}
                            data-tb-test-id={'username'}
                            value={props.username}
                            maxLength={255}
                            label={Messages.username()}
                        />
                    </div>
                </div>

                <div className={rowClassname} style={{ paddingTop: '16px' }} >
                    <div className={rowClassname}>
                        <TextFieldWidget
                            className={rowClassname}
                            style={{ width: '100%' }}
                            onChange={e => props.handlePasswordChange(e.target.value)}
                            testId={'username'}
                            data-tb-test-id={'username'}
                            value={props.password}
                            maxLength={255}
                            label={Messages.password()}
                            type={TextInputType.Password}
                        />
                    </div>
                </div>

            </>
        );
    };
    const renderOAuthCredentialOptionSelector = (): React.ReactNode => {
        if (props.credentialOption !== 'oauth') {
            return null;
        }

        let items: ListItem[] = [];
        if (props.credentials) {
            items = getIOAuthCredentialIdsList(props.credentials);
        }
        if (props.addNewOAuthCredentialOptionValue) {
            items.push({ value: props.addNewOAuthCredentialOptionValue, content: Messages.addOAuthCredential({ CONNECTION_TYPE_NAME: props.currentConnectionTypeDisplayName }) });
        }
        const listProps: ListProps = { items };

        return (
            <SingleSelectDropdown
                label=''
                list={<List {...listProps} />}
                onChange={props.handleCredentialIdChange}
                type={DropdownButtonType.Outline}
                value={props.credentialId}
                style={{ marginBottom: TabStyles.Sizing.BaseUnit }}
            >
                {getNameForIOAuthCredentialId(props.credentialId, items)}
            </SingleSelectDropdown>
        );
    };

    const renderNoAuthOption = (): React.ReactNode => {
        return (
            < div className={option}>
                <div className={optionText}>{Messages.editDataConnections_generic_noCredential()}</div>
                <label className={optionSubText}>{Messages.editDataConnections_generic_describeNoCredential()}</label>
            </div>
        );
    };

    return (
        <>
            <div className={authenticationLabel}>{Messages.authentication()}</div>
            <RadioButtonWithLabelGroupWidget
                buttonList={[
                    {
                        label: renderUsernamePassword(),
                        testId: 'password',
                        value: 'password'
                    },
                    {
                        label: renderOAuthCredentialOption(),
                        testId: 'embedded',
                        value: 'oauth',
                        disabled: (!props.credentials || props.credentials.length === 0) && !props.addNewOAuthCredentialOptionValue,
                    },
                    {
                        label: renderNoAuthOption(),
                        testId: 'no-auth',
                        value: 'none',
                    },
                ]}
                checkedIndex={props.credentialOption === 'password' ? 0 : (props.credentialOption === 'oauth' ? 1 : 2)}
                handleChange={(value) => props.handleToggleChange(value)}
            />
        </>
    );
}
